"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.mapAppBaseMarkers = mapAppBaseMarkers;
exports.appBaseMarkerValues = appBaseMarkerValues;
exports.mapAppBaseTemplateTextMarkers = mapAppBaseTemplateTextMarkers;
/**
 * The forms a page names a marker in, each with a literal value: the HTML
 * attribute, the key of an attribute object (`h('button', { 'data-sl-action':
 * 'save' })`), `setAttribute('data-sl-action', 'save')` and `dataset.slAction
 * = 'save'`. A computed value is no marker. Each form reads `(head)(art)` and
 * the quoted value; the forms never overlap, so one pass per form maps a
 * value once.
 */
function appBaseMarkerForms(arts) {
    const names = arts.join('|');
    const datasets = arts
        .map(art => art
        .slice('data-'.length)
        .replace(/-([a-z])/g, (_match, letter) => letter.toUpperCase()))
        .join('|');
    const value = '((?:(?!\\$\\{)[^\\n])*?)';
    return [
        {
            pattern: new RegExp(`(\\s(${names})\\s*=\\s*)(["'])${value}\\3`, 'g'),
            art: 2,
            quote: 3,
            value: 4,
        },
        {
            pattern: new RegExp(`((["'])(${names})\\2\\s*:\\s*)(["'\`])${value}\\4`, 'g'),
            art: 3,
            quote: 4,
            value: 5,
        },
        {
            pattern: new RegExp(`(\\.setAttribute\\(\\s*(["'])(${names})\\2\\s*,\\s*)(["'\`])${value}\\4`, 'g'),
            art: 3,
            quote: 4,
            value: 5,
        },
        {
            pattern: new RegExp(`(\\.dataset\\.(${datasets})\\s*=\\s*)(["'\`])${value}\\3`, 'g'),
            art: 2,
            quote: 3,
            value: 4,
            dataset: true,
        },
    ];
}
/** The art of a marker as its attribute: `slAction` is `data-sl-action`. */
const markerArt = (name, dataset) => dataset
    ? `data-${name.replace(/[A-Z]/g, letter => `-${letter.toLowerCase()}`)}`
    : name;
/**
 * `text` with the value of each marker of `arts` that `mapped` names
 * replaced, in every form a page names it in; `mapped` answers undefined to
 * keep a value.
 */
function mapAppBaseMarkers(text, arts, mapped) {
    return appBaseMarkerForms(arts).reduce((current, form) => current.replace(form.pattern, (...match) => {
        const next = mapped(markerArt(match[form.art], form.dataset), match[form.value]);
        return next === undefined
            ? match[0]
            : `${match[1]}${match[form.quote]}${next}${match[form.quote]}`;
    }), text);
}
/** Every marker of `arts` the text names with a literal value, as `art` → values. */
function appBaseMarkerValues(text, arts) {
    const found = new Map();
    mapAppBaseMarkers(text, arts, (art, value) => {
        let values = found.get(art);
        if (!values)
            found.set(art, (values = new Set()));
        values.add(value);
        return undefined;
    });
    return found;
}
function mapAppBaseTemplateTextMarkers(text, input) {
    if (text === undefined)
        return text;
    const routes = new Map(input.routePaths.map(path => [path, input.mapRoutePath(path)]));
    const sources = new Map(input.sourceKeys.map(key => [key, input.mapSourceKey(key)]));
    const actions = new Map(input.actions.map(action => [action.id, input.mapActionId(action.id)]));
    const exports = new Map(input.documentExports.map(item => [item.id, input.mapExportId(item.id)]));
    const controls = new Map();
    for (const id of input.navigationActionIds ?? [])
        controls.set(id, input.mapActionId(id));
    for (const routePath of input.routePaths) {
        controls.set(`route:${routePath}`, `route:${routes.get(routePath)}`);
    }
    for (const sourceKey of input.sourceKeys) {
        controls.set(`dataset:${sourceKey}`, `dataset:${sources.get(sourceKey)}`);
    }
    for (const action of input.actions) {
        const mappedActionId = actions.get(action.id);
        controls.set(`action:${action.id}`, `action:${mappedActionId}`);
        controls.set(`status:${action.id}`, `status:${mappedActionId}`);
        if (action.type === 'rowInsert' || action.type === 'automationRun') {
            for (const field of action.fields ?? []) {
                controls.set(`action:${action.id}:field:${field.columnId}`, `action:${mappedActionId}:field:${field.columnId}`);
            }
        }
    }
    for (const documentExport of input.documentExports) {
        controls.set(`export:${documentExport.id}`, `export:${exports.get(documentExport.id)}`);
    }
    const markers = {
        'data-sl-route': routes,
        'data-sl-source': sources,
        'data-sl-filter-source': sources,
        'data-sl-action': actions,
        'data-sl-export': exports,
        'data-sl-control': controls,
    };
    // Each value is mapped once, so a mapped name is never mapped again when
    // source routes or identifiers overlap with a mount's destination namespace.
    return mapAppBaseMarkers(text, Object.keys(markers), (marker, value) => markers[marker].get(value));
}
